import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, ArrowLeftRight, CheckCircle2, XCircle, Target,
  Lightbulb, Building2, Scale, TrendingUp
} from 'lucide-react';
import { whyIpFundersContent } from '../data/mock';

const WhyIpFundersPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-16 pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-20 w-64 h-64 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#1e3a5f]/10 text-[#1e3a5f] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Target className="w-4 h-4" />
            <span>Our Purpose</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1f2e] mb-6">
            Why IpFunders Exists
          </h1>
          
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            To reduce the risk of creating intellectual property — so innovation can move forward on merit, not just capital.
          </p>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-slate prose-lg max-w-none">
                {whyIpFundersContent.manifesto.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-slate-600 leading-relaxed mb-6 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What We Are / Are Not */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0f1f2e] mb-4">
              Clear Positioning
            </h2>
            <p className="text-lg text-slate-600">
              Understanding what IpFunders is — and isn't
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyIpFundersContent.positioning.map((section, idx) => (
              <Card key={idx} className={`border-2 ${
                idx === 0 
                  ? 'bg-emerald-50/50 border-emerald-200' 
                  : 'bg-white border-slate-200'
              }`}>
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      idx === 0 ? 'bg-emerald-500' : 'bg-slate-300'
                    }`}>
                      {idx === 0 
                        ? <CheckCircle2 className="w-5 h-5 text-white" />
                        : <XCircle className="w-5 h-5 text-white" />
                      }
                    </div>
                    <h3 className="text-xl font-bold text-[#0f1f2e]">{section.title}</h3>
                  </div>
                  <ul className="space-y-4">
                    {section.points.map((point, pointIdx) => (
                      <li key={pointIdx} className="flex items-start space-x-3">
                        {idx === 0 
                          ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          : <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        }
                        <span className="text-slate-600">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Four Types of Risk */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0f1f2e] mb-4">
              The Risk We Shift
            </h2>
            <p className="text-lg text-slate-600">
              Traditional IP models only talk about financial risk. We address all four.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyIpFundersContent.riskTypes.map((risk, idx) => (
              <Card key={idx} className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-[#1e3a5f] rounded-lg flex items-center justify-center mb-4">
                    <span className="text-white font-bold">{idx + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2">{risk.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{risk.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-2 bg-emerald-50 text-emerald-700 px-6 py-3 rounded-full">
              <ArrowLeftRight className="w-5 h-5" />
              <span className="font-medium">All four risks shifted from innovator to IpFunders</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Insight */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-emerald-400 text-sm font-medium mb-4">THE CORE INSIGHT</div>
          <blockquote className="text-2xl md:text-3xl font-bold text-white leading-relaxed mb-8">
            "IpFunders absorbs the upfront risk of creating IP assets, and later recovers value if — and only if — the IP proves valuable."
          </blockquote>
          <p className="text-slate-300 text-lg">
            That's risk shifting, not just funding. That's why we exist.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#0f1f2e] mb-6">
            See How It Works in Practice
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            Understand the process, or submit your concept for evaluation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white px-8 py-6 text-base">
                How It Works
              </Button>
            </Link>
            <Link to="/request-access">
              <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2a4a73] text-white px-8 py-6 text-base shadow-lg">
                Request Evaluation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyIpFundersPage;
